<template>
  <v-dialog :value="value" max-width="480" @input="$emit('input', $event)">
    <v-card class="online-appointment-message-preview-dialog px-8 pt-6 pb-7">
      <v-card-title class="px-0 text-h5 font-weight-bold">
        {{ title }}
      </v-card-title>
      <v-card-text class="px-0">
        <div class="grey--text text--darken-1">
          以下是將發送給病人的訊息
        </div>

        <template v-if="loading">
          <v-skeleton-loader class="mt-5" type="paragraph, actions" />
        </template>

        <template v-else>
          <div class="d-flex align-center mt-5">
            <v-icon left color="grey-5">
              {{ isSmsChannel ? 'mdi-message-text-outline' : 'mdi-chat-outline' }}
            </v-icon>
            <span class="grey--text text--darken-1">{{ channelText }}</span>
          </div>

          <div class="mt-5 text-body-2 font-weight-medium">
            將送出的內容（預覽）
          </div>
          <v-textarea
            v-if="isEditing"
            v-model="localContent"
            outlined
            auto-grow
            rows="3"
            hide-details="auto"
            :error-messages="inputErrorMessages"
            class="mt-2"
          />
          <v-sheet
            v-else
            rounded
            outlined
            class="mt-2 pa-4 online-appointment-message-preview-dialog__content"
          >
            {{ activeContent }}
          </v-sheet>

          <div class="mt-2 text-right text-caption grey--text text--darken-1">
            <div>{{ characterCount }} / {{ maxCharacters }}</div>
            <div v-if="isSmsChannel" class="mt-1">
              {{ characterCount }} 字 / {{ smsSegments }} 封
              <span :class="{ 'red-6--text font-weight-medium': isSmsQuotaBlocked }">
                ・需 {{ smsSegments }} 點
              </span>
            </div>
            <div v-else class="mt-1">{{ characterCount }} 字</div>
          </div>

          <template v-if="isReject && visibleReasonTemplates.length && !isEditing">
            <div class="mt-5 text-body-2 grey--text text--darken-1">
              套用不成功原因（可選）
            </div>
            <div class="mt-2 d-flex flex-wrap">
              <v-btn
                v-for="reasonTemplate in visibleReasonTemplates"
                :key="reasonTemplate.template_key"
                small
                rounded
                outlined
                class="mr-2 mb-2"
                :color="selectedReason && selectedReason.template_key === reasonTemplate.template_key ? 'primary' : 'grey'"
                @click="selectReason(reasonTemplate)"
              >
                {{ reasonTemplate.reason }}
              </v-btn>
            </div>
          </template>

          <v-sheet
            v-if="isSmsQuotaBlocked"
            rounded
            color="red-1"
            class="online-appointment-message-preview-dialog__quota-alert mt-4 pa-4"
          >
            <div class="d-flex align-start">
              <v-icon color="red-6" class="mr-3 mt-1">mdi-alert-outline</v-icon>
              <div>
                <div class="red-6--text font-weight-bold">點數不足，此訊息無法發送</div>
                <div class="mt-1 grey-6--text">
                  目前剩餘 {{ customizedQuota }} 點、需 {{ smsSegments }} 點，儲值後即可發送
                </div>
              </div>
            </div>
          </v-sheet>
          <v-alert
            v-else-if="blockMessage"
            dense
            text
            type="warning"
            class="mt-4 mb-0"
          >
            {{ blockMessage }}
          </v-alert>

          <v-btn
            text
            small
            color="tiffany-7"
            class="mt-4 px-0"
            @click="isEditing ? resetToPublicContent() : startCustomize()"
          >
            <v-icon small left>{{ isEditing ? 'mdi-undo' : 'mdi-pencil-outline' }}</v-icon>
            {{ customizeButtonText }}
          </v-btn>

          <v-sheet
            v-if="showSmsSendNotes"
            rounded
            outlined
            class="mt-4"
          >
            <v-expansion-panels flat accordion>
              <v-expansion-panel>
                <v-expansion-panel-header class="text-body-2 font-weight-medium grey--text text--darken-1">
                  <div class="d-flex align-center">
                    <v-icon small color="grey-5" class="mr-2">mdi-information-outline</v-icon>
                    簡訊發送須知
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="text-caption grey--text text--darken-2">
                    <div>・備註一：輸入的字數與實際發送的字數可能有些微差異，將會依照實際發送的字數來扣點</div>
                    <div class="mt-2">・備註二：近期政府推動反詐騙宣導，若內文涉及敏感字眼，可能會有被阻擋發送之風險，若有遇到發送不成功的情況請再洽詢牙醫小幫手客服</div>
                    <div class="mt-2">・備註三：承上則，每封客製化簡訊將會經由電信企業審核，無詐騙疑慮才能送出，審核時間長度不一，無法立即送達，敬請耐心等候。</div>
                    <div class="mt-2">・備註四：請勿在訊息內放入網址連結，否則可能會被判定為詐騙簡訊而導致發送失敗。</div>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-sheet>
        </template>
      </v-card-text>
      <v-card-actions class="px-0 pt-0 mt-4">
        <v-row v-if="isSmsQuotaBlocked" no-gutters>
          <v-col cols="12">
            <v-btn
              block
              depressed
              color="tiffany-7"
              class="white--text font-weight-bold"
              :disabled="confirming"
              :href="$enum.DENTCO_CS_LINE"
              target="_blank"
            >
              聯繫客服儲值
            </v-btn>
          </v-col>
          <v-col v-if="showSelfCallButton" cols="12" class="mt-3">
            <v-btn
              block
              outlined
              color="grey-5"
              class="font-weight-bold"
              :loading="confirming"
              @click="selfCall"
            >
              我會自行致電通知患者
            </v-btn>
          </v-col>
          <v-col cols="12" class="mt-4 text-center">
            <v-btn text color="grey-5" :disabled="confirming" @click="$emit('input', false)">
              取消
            </v-btn>
          </v-col>
        </v-row>
        <v-row v-else no-gutters align="start">
          <v-col cols="auto">
            <v-btn color="grey-5" class="white--text mt-2" :disabled="confirming" @click="$emit('input', false)">
              取消
            </v-btn>
          </v-col>
          <v-col class="d-flex justify-end flex-wrap">
            <v-btn
              v-if="showSelfCallButton"
              color="orange-4"
              dark
              class="mt-2 ml-2"
              :loading="confirming"
              @click="selfCall"
            >
              我會自行致電通知患者
            </v-btn>
            <v-btn
              v-if="showInsertButton"
              color="blue-6"
              dark
              class="mt-2 ml-2"
              :loading="confirming"
              :disabled="!canConfirm"
              @click="confirm(true)"
            >
              {{ insertSystemText }}
            </v-btn>
            <v-btn
              color="primary"
              class="mt-2 ml-2"
              :loading="confirming"
              :disabled="!canConfirm"
              @click="confirm(false)"
            >
              確認發送
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>

      <v-dialog v-model="customQuotaDialog" max-width="420">
        <v-card class="pa-6 text-center">
          <v-card-title class="justify-center font-weight-bold">
            需要儲值簡訊點數才能使用客製化功能
          </v-card-title>
          <v-card-text>
            {{ customQuotaDialogMessage }}
          </v-card-text>
          <v-card-actions class="justify-center">
            <v-btn outlined color="grey-5" @click="customQuotaDialog = false">
              取消
            </v-btn>
            <v-btn color="tiffany-7" depressed class="white--text" :href="$enum.DENTCO_CS_LINE" target="_blank">
              聯繫客服儲值
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  calculateOnlineAppointmentSmsSegments,
  MAX_ONLINE_APPOINTMENT_MESSAGE_CHARACTERS,
  ONLINE_APPOINTMENT_TEMPLATE_KEYS
} from "@/constants/onlineAppointmentMessages";

export default {
  name: "AppointmentMessagePreviewDialog",

  props: {
    value: {
      type: Boolean,
      required: true,
    },
    preview: {
      type: Object,
      default: null,
    },
    actionType: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    confirming: {
      type: Boolean,
      default: false,
    },
    insertAvailable: {
      type: Boolean,
      default: false,
    },
    insertSystemText: {
      type: String,
      default: '點擊寫入約診系統',
    },
    maxCharacters: {
      type: Number,
      default: MAX_ONLINE_APPOINTMENT_MESSAGE_CHARACTERS,
    },
  },

  data() {
    return {
      isEditing: false,
      isShowingInitialContent: true,
      localContent: '',
      selectedReason: null,
      customQuotaDialog: false,
    }
  },

  computed: {
    isReject() {
      return this.actionType === 'reject'
    },
    isNotificationProcessAction() {
      return ['allow', 'reject'].includes(this.actionType)
    },
    title() {
      return this.isReject ? '確認預約不成功訊息' : '確認預約成功訊息'
    },
    isSmsChannel() {
      return this.preview?.channel !== 'line'
    },
    channelText() {
      return this.isSmsChannel ? '本次以簡訊發送' : '本次以 LINE 發送'
    },
    reasonTemplates() {
      return this.preview?.reason_templates || []
    },
    visibleReasonTemplates() {
      return this.reasonTemplates.filter((reasonTemplate) => reasonTemplate?.reason !== '不修改訊息內容')
    },
    initialTemplateKey() {
      if (this.preview?.initial_template_key) return this.preview.initial_template_key
      return this.isReject
        ? ONLINE_APPOINTMENT_TEMPLATE_KEYS.FAILED
        : ONLINE_APPOINTMENT_TEMPLATE_KEYS.CONFIRMED
    },
    publicTemplateKey() {
      return this.isReject
        ? ONLINE_APPOINTMENT_TEMPLATE_KEYS.FAILED
        : ONLINE_APPOINTMENT_TEMPLATE_KEYS.CONFIRMED
    },
    initialContent() {
      return this.preview?.initial_content ||
        this.preview?.default_content ||
        this.preview?.preview ||
        ''
    },
    publicContent() {
      return this.preview?.default_content ||
        this.preview?.default_template?.content ||
        this.preview?.preview ||
        ''
    },
    activeContent() {
      if (this.isEditing) return this.localContent
      if (this.selectedReason) return this.selectedReason.rendered_content || ''
      if (this.isShowingInitialContent) return this.initialContent
      return this.publicContent
    },
    activeTemplateKey() {
      if (this.selectedReason) return this.selectedReason.template_key
      if (this.isShowingInitialContent) return this.initialTemplateKey
      return this.publicTemplateKey
    },
    completedReason() {
      return this.selectedReason?.reason || null
    },
    characterCount() {
      if (!this.isEditing && !this.selectedReason && this.isShowingInitialContent && this.preview?.character_count) {
        return Number(this.preview.character_count)
      }
      return (this.activeContent || '').length
    },
    smsSegments() {
      if (!this.isEditing && this.selectedReason?.sms_segments) {
        return Number(this.selectedReason.sms_segments)
      }
      if (!this.isEditing && !this.selectedReason && this.isShowingInitialContent && this.preview?.sms_segments) {
        return Number(this.preview.sms_segments)
      }
      return calculateOnlineAppointmentSmsSegments(this.characterCount)
    },
    customizedQuota() {
      return Number(this.preview?.customized_quota || 0)
    },
    hasCustomizedQuota() {
      return this.preview?.customized_quota !== undefined &&
        this.preview?.customized_quota !== null &&
        this.preview?.customized_quota !== ''
    },
    customizedPayerTypeText() {
      return this.preview?.customized_payer_type_text || '診所'
    },
    customizedQuotaSubject() {
      return `${this.customizedPayerTypeText}簡訊點數`
    },
    canEditCustomized() {
      return ![false, 0, '0', 'false'].includes(this.preview?.can_edit_customized)
    },
    customQuotaDialogMessage() {
      if (this.preview?.customized_block_code === 'SMS_QUOTA_REQUIRED') {
        return `目前${this.customizedPayerTypeText}尚未儲值簡訊點數，無法客製化此則簡訊。`
      }
      return `目前${this.customizedQuotaSubject}不足，無法客製化此則簡訊。`
    },
    hasLengthError() {
      return this.characterCount > this.maxCharacters
    },
    hasQuotaError() {
      return this.isSmsChannel && this.isEditing && this.hasCustomizedQuota && this.smsSegments > this.customizedQuota
    },
    blockCode() {
      return this.preview?.block_code || ''
    },
    isInitialCustomizedContent() {
      return String(this.preview?.template_source) === '1' ||
        String(this.preview?.effective_template_source) === '1'
    },
    isUsingCustomizedContent() {
      if (this.isEditing) return true
      if (this.selectedReason) return false
      if (this.isShowingInitialContent) return this.isInitialCustomizedContent
      return false
    },
    previewBlocked() {
      if (this.preview?.can_send !== false) return false
      if (['SMS_NO_BALANCE', 'SMS_QUOTA_REQUIRED'].includes(this.blockCode)) {
        if (!this.isSmsChannel || !this.isUsingCustomizedContent) return false
        if (this.isEditing && this.blockCode === 'SMS_NO_BALANCE') return false
      }
      return true
    },
    isSmsQuotaBlocked() {
      return this.isSmsChannel && (
        this.hasQuotaError ||
        (
          this.preview?.can_send === false &&
          ['SMS_NO_BALANCE', 'SMS_QUOTA_REQUIRED'].includes(this.blockCode) &&
          this.isUsingCustomizedContent
        )
      )
    },
    blockMessage() {
      if (this.hasLengthError) return `訊息內容最多 ${this.maxCharacters} 字`
      if (this.hasQuotaError) return `${this.customizedQuotaSubject}不足，目前剩餘 ${this.customizedQuota} 點，此訊息預估需要 ${this.smsSegments} 點`
      if (!this.previewBlocked) return ''

      switch (this.blockCode) {
        case 'SMS_NO_BALANCE':
          return `${this.customizedQuotaSubject}不足，無法發送客製化簡訊`
        case 'SMS_QUOTA_REQUIRED':
          return `需要儲值${this.customizedQuotaSubject}才能發送客製化簡訊`
        case 'LINE_NO_BALANCE':
          return 'LINE 訊息額度不足，無法發送'
        default:
          return '目前無法發送此則訊息'
      }
    },
    customizeButtonText() {
      if (!this.isEditing) return '客製化訊息'
      if (this.isSmsQuotaBlocked) return '切回公版內容（公版免費，可直接發送）'
      return '切回公版內容'
    },
    inputErrorMessages() {
      const messages = []
      if (this.hasLengthError) messages.push(`訊息內容最多 ${this.maxCharacters} 字`)
      return messages
    },
    canConfirm() {
      return !this.loading && !this.confirming && !this.hasLengthError && !this.hasQuotaError && !this.previewBlocked
    },
    showInsertButton() {
      return this.insertAvailable && !this.isSmsQuotaBlocked
    },
    showSelfCallButton() {
      return this.isNotificationProcessAction && (this.previewBlocked || this.hasQuotaError)
    },
    showSmsSendNotes() {
      return !this.loading && this.isSmsChannel
    },
  },

  watch: {
    value(value) {
      if (value) this.resetFromPreview()
    },
    preview() {
      if (this.value) this.resetFromPreview()
    },
  },

  methods: {
    resetFromPreview() {
      this.selectedReason = null
      this.isShowingInitialContent = true
      this.localContent = this.initialContent
      this.isEditing = String(this.preview?.template_source) === '1'
      if (this.isEditing) this.localContent = this.initialContent
    },
    selectReason(reasonTemplate) {
      if (this.selectedReason?.template_key === reasonTemplate.template_key) {
        this.selectedReason = null
        return
      }
      this.selectedReason = reasonTemplate
      this.isShowingInitialContent = false
    },
    startCustomize() {
      if (!this.canEditCustomized) {
        this.customQuotaDialog = true
        return
      }
      this.localContent = this.activeContent
      this.isEditing = true
      this.isShowingInitialContent = false
    },
    resetToPublicContent() {
      this.isEditing = false
      this.localContent = this.publicContent
      this.selectedReason = null
      this.isShowingInitialContent = false
    },
    confirm(insertToHis) {
      if (!this.canConfirm) return
      this.$emit('confirm', {
        insert_to_his: insertToHis ? 1 : undefined,
        message_content: this.activeContent,
        message_template_key: this.activeTemplateKey,
        completed_reason: this.completedReason,
      })
    },
    selfCall() {
      this.$emit('self-call', {
        notification_action: 'self_call',
        completed_reason: this.completedReason,
      })
    },
  },
}
</script>

<style scoped>
.online-appointment-message-preview-dialog {
  border-radius: 14px;
}

.online-appointment-message-preview-dialog__content {
  min-height: 84px;
  line-height: 1.7;
  white-space: pre-line;
}

.online-appointment-message-preview-dialog__quota-alert {
  border-left: 4px solid var(--v-red-6-base);
}
</style>
